import React, { useEffect, useMemo, useState } from "react";
import "./SecaoBloqueioBase.css";
import {
  alterarStatusSecaoBloqueio,
  cadastrarSecaoBloqueio,
  editarSecaoBloqueio,
  excluirSecaoBloqueio,
  listarSecoesBloqueio,
} from "./secaoBloqueio.api";

const initialForm = {
  secao_bloqueio: "",
  sub: "",
  km_inicial: "",
  km_final: "",
  mesa_cco: "",
};

const formatSafe = (value) => (value == null || value === "" ?"-" : String(value));
const formatKm = (value) =>
  value == null || value === "" ?"-" : String(value).replace(".", ",");

function sanitizeCode(value, max = 80) {
  return String(value ?? "")
    .replace(/[\u0000-\u001F\u007F]/g, "")
    .replace(/[<>]/g, "")
    .trim()
    .toLocaleUpperCase("pt-BR")
    .slice(0, max);
}

function sanitizeKm(value) {
  return String(value ?? "")
    .replace(",", ".")
    .replace(/[^\d.]/g, "")
    .trim()
    .slice(0, 16);
}

function sanitizeSub(value) {
  return String(value ?? "")
    .replace(/\.0+$/, "")
    .replace(/[^\d]/g, "")
    .trim()
    .slice(0, 10);
}

function ToggleSwitch({ on = false, onToggle, title }) {
  return (
    <button
      type="button"
      aria-pressed={on}
      className={`scb-toggle ${on ?"on" : "off"}`}
      onClick={onToggle}
      title={title || (on ?"Inativar" : "Ativar")}
    >
      <span className="scb-toggle-knob" />
    </button>
  );
}

function IconEdit(props) {
  return (
    <svg
      viewBox="0 0 24 24"
      width="18"
      height="18"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      {...props}
    >
      <path d="M12 20h9" />
      <path d="M16.5 3.5a2.121 2.121 0 1 1 3 3L7 19l-4 1 1-4 12.5-12.5z" />
    </svg>
  );
}

function IconTrash(props) {
  return (
    <svg
      viewBox="0 0 24 24"
      width="18"
      height="18"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      {...props}
    >
      <polyline points="3 6 5 6 21 6" />
      <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
      <path d="M10 11v6M14 11v6" />
      <path d="M9 6V4a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2" />
    </svg>
  );
}

export default function SecaoBloqueioBase() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [feedback, setFeedback] = useState("");
  const [filtroTexto, setFiltroTexto] = useState("");
  const [filtroStatus, setFiltroStatus] = useState("todos");
  const [form, setForm] = useState(initialForm);
  const [editingId, setEditingId] = useState(null);

  async function carregar() {
    setLoading(true);
    setError("");
    try {
      const ativo =
        filtroStatus === "ativos" ?1 : filtroStatus === "inativos" ?0 : undefined;
      const result = await listarSecoesBloqueio({
        q: filtroTexto,
        ativo,
        limit: 5000,
        offset: 0,
      });
      setRows(result.rows || []);
    } catch (err) {
      setRows([]);
      setError(err?.message || "Erro ao carregar a base de Seção de Bloqueio.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    carregar();
  }, [filtroTexto, filtroStatus]);

  const collator = useMemo(
    () =>
      new Intl.Collator("pt-BR", {
        sensitivity: "base",
        numeric: true,
      }),
    []
  );

  const stats = useMemo(() => {
    const total = rows.length;
    const ativos = rows.filter((row) => Number(row?.ativo) === 1).length;
    return {
      total,
      ativos,
      inativos: total - ativos,
    };
  }, [rows]);

  const rowsOrdenadas = useMemo(
    () =>
      [...rows].sort((a, b) => {
        const subA = Number.parseInt(a?.sub, 10);
        const subB = Number.parseInt(b?.sub, 10);
        const safeSubA = Number.isFinite(subA) ?subA : Number.MAX_SAFE_INTEGER;
        const safeSubB = Number.isFinite(subB) ?subB : Number.MAX_SAFE_INTEGER;
        if (safeSubA !== safeSubB) return safeSubA - safeSubB;

        const kmA = Number.parseFloat(a?.km_inicial);
        const kmB = Number.parseFloat(b?.km_inicial);
        const safeKmA = Number.isFinite(kmA) ?kmA : Number.MAX_SAFE_INTEGER;
        const safeKmB = Number.isFinite(kmB) ?kmB : Number.MAX_SAFE_INTEGER;
        if (safeKmA !== safeKmB) return safeKmA - safeKmB;

        const kmFinalA = Number.parseFloat(a?.km_final);
        const kmFinalB = Number.parseFloat(b?.km_final);
        const safeKmFinalA = Number.isFinite(kmFinalA) ?kmFinalA : Number.MAX_SAFE_INTEGER;
        const safeKmFinalB = Number.isFinite(kmFinalB) ?kmFinalB : Number.MAX_SAFE_INTEGER;
        if (safeKmFinalA !== safeKmFinalB) return safeKmFinalA - safeKmFinalB;

        const secaoA = `${a?.secao_bloqueio || a?.secaodebloqueio || a?.sb || ""}`;
        const secaoB = `${b?.secao_bloqueio || b?.secaodebloqueio || b?.sb || ""}`;
        const bySecao = collator.compare(secaoA, secaoB);
        if (bySecao !== 0) return bySecao;

        return Number(a?.id || 0) - Number(b?.id || 0);
      }),
    [collator, rows]
  );

  function resetForm() {
    setForm(initialForm);
    setEditingId(null);
  }

  function handleChange(field, value) {
    setForm((prev) => ({
      ...prev,
      [field]:
        field === "sub"
          ?sanitizeSub(value)
          : field === "km_inicial" || field === "km_final"
          ?sanitizeKm(value)
          : sanitizeCode(value),
    }));
  }

  async function handleSubmit(event) {
    event.preventDefault();
    setSaving(true);
    setError("");
    setFeedback("");

    try {
      const payload = {
        secao_bloqueio: sanitizeCode(form.secao_bloqueio, 50),
        sub: sanitizeSub(form.sub),
        km_inicial: sanitizeKm(form.km_inicial),
        km_final: sanitizeKm(form.km_final),
        mesa_cco: sanitizeCode(form.mesa_cco, 80),
      };

      if (
        !payload.secao_bloqueio ||
        !payload.sub ||
        !payload.km_inicial ||
        !payload.km_final ||
        !payload.mesa_cco
      ) {
        throw new Error(
          "Preencha Seção de Bloqueio, SUB, KM inicial, KM final e Mesa CCO."
        );
      }

      if (editingId) {
        await editarSecaoBloqueio(editingId, payload);
        setFeedback("Seção de Bloqueio atualizada com sucesso.");
      } else {
        await cadastrarSecaoBloqueio(payload);
        setFeedback("Seção de Bloqueio cadastrada com sucesso.");
      }

      resetForm();
      await carregar();
    } catch (err) {
      setError(err?.message || "Não foi possível salvar a Seção de Bloqueio.");
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(row) {
    if (!row?.id) return;
    const secaoBloqueio = row?.secao_bloqueio || row?.secaodebloqueio || row?.sb || "-";
    const ok = window.confirm(`Excluir a Seção de Bloqueio ${secaoBloqueio}?`);
    if (!ok) return;

    try {
      await excluirSecaoBloqueio(row.id);
      setFeedback("Seção de Bloqueio excluída com sucesso.");
      if (editingId === row.id) resetForm();
      await carregar();
    } catch (err) {
      setError(err?.message || "Não foi possível excluir a Seção de Bloqueio.");
    }
  }

  async function handleToggle(row) {
    if (!row?.id) return;
    try {
      await alterarStatusSecaoBloqueio(row.id, Number(row?.ativo) === 1 ?0 : 1);
      setFeedback("Status da Seção de Bloqueio atualizado.");
      await carregar();
    } catch (err) {
      setError(err?.message || "Não foi possível alterar o status.");
    }
  }

  function handleEdit(row) {
    setEditingId(row?.id || null);
    setForm({
      secao_bloqueio: row?.secao_bloqueio || row?.secaodebloqueio || row?.sb || "",
      sub: row?.sub != null ?String(row.sub) : "",
      km_inicial: row?.km_inicial != null ?String(row.km_inicial).replace(".", ",") : "",
      km_final: row?.km_final != null ?String(row.km_final).replace(".", ",") : "",
      mesa_cco: row?.mesa_cco != null ?String(row.mesa_cco) : "",
    });
    setFeedback("");
    setError("");
  }

  return (
    <div className="scb-base">
      <div className="scb-cards">
        <div className="scb-card">
          <span>Total</span>
          <strong>{stats.total}</strong>
        </div>
        <div className="scb-card">
          <span>Ativas</span>
          <strong>{stats.ativos}</strong>
        </div>
        <div className="scb-card">
          <span>Inativas</span>
          <strong>{stats.inativos}</strong>
        </div>
      </div>

      <section className="scb-form-panel">
        <div className="scb-form-head">
          <div>
            <h4>{editingId ?"Editar Seção de Bloqueio" : "Cadastrar Seção de Bloqueio"}</h4>
            <p>Cadastre a seção de bloqueio com a SUB, faixa de KM e Mesa CCO.</p>
          </div>
          {editingId ?(
            <button type="button" className="scb-btn scb-btn--ghost" onClick={resetForm}>
              Novo cadastro
            </button>
          ) : null}
        </div>

        <form className="scb-form-grid" onSubmit={handleSubmit}>
          <label className="scb-field">
            <span>Seção de Bloqueio</span>
            <input
              type="text"
              value={form.secao_bloqueio}
              onChange={(event) => handleChange("secao_bloqueio", event.target.value)}
              placeholder="Ex.: 38.0"
            />
          </label>

          <label className="scb-field">
            <span>SUB</span>
            <input
              type="text"
              inputMode="numeric"
              value={form.sub}
              onChange={(event) => handleChange("sub", event.target.value)}
              placeholder="Ex.: 1"
            />
          </label>

          <label className="scb-field">
            <span>KM inicial</span>
            <input
              type="text"
              inputMode="decimal"
              value={form.km_inicial}
              onChange={(event) => handleChange("km_inicial", event.target.value)}
              placeholder="Ex.: 40513"
            />
          </label>

          <label className="scb-field">
            <span>KM final</span>
            <input
              type="text"
              inputMode="decimal"
              value={form.km_final}
              onChange={(event) => handleChange("km_final", event.target.value)}
              placeholder="Ex.: 41276"
            />
          </label>

          <label className="scb-field">
            <span>Mesa CCO</span>
            <input
              type="text"
              value={form.mesa_cco}
              onChange={(event) => handleChange("mesa_cco", event.target.value)}
              placeholder="Ex.: CCO 1"
            />
          </label>

          <div className="scb-form-actions">
            <button type="submit" className="scb-btn scb-btn--primary" disabled={saving}>
              {saving ?"Salvando..." : editingId ?"Salvar alterações" : "Cadastrar seção"}
            </button>
          </div>
        </form>

        <p className={`scb-feedback ${error ?"is-error" : ""}`}>
          {error || feedback || "Use este cadastro para alimentar as telas do CIM."}
        </p>
      </section>

      <section className="scb-table-panel">
        <div className="scb-toolbar">
          <input
            type="text"
            value={filtroTexto}
            onChange={(event) => setFiltroTexto(event.target.value)}
            placeholder="Buscar seção, SUB, mesa ou KM..."
          />

          <select value={filtroStatus} onChange={(event) => setFiltroStatus(event.target.value)}>
            <option value="todos">Todos os status</option>
            <option value="ativos">Somente ativos</option>
            <option value="inativos">Somente inativos</option>
          </select>
        </div>

        <div className="scb-table-wrap">
          <table className="scb-table scb-table--head">
            <colgroup>
              <col style={{ width: "18%" }} />
              <col style={{ width: "14%" }} />
              <col style={{ width: "14%" }} />
              <col style={{ width: "14%" }} />
              <col style={{ width: "22%" }} />
              <col style={{ width: "18%" }} />
            </colgroup>
            <thead>
              <tr>
                <th>Seção de Bloqueio</th>
                <th>SUB</th>
                <th>KM inicial</th>
                <th>KM final</th>
                <th>Mesa CCO</th>
                <th>Controles</th>
              </tr>
            </thead>
          </table>

          <div className="scb-table-scroll">
            <table className="scb-table scb-table--body">
              <colgroup>
                <col style={{ width: "18%" }} />
                <col style={{ width: "14%" }} />
                <col style={{ width: "14%" }} />
                <col style={{ width: "14%" }} />
                <col style={{ width: "22%" }} />
                <col style={{ width: "18%" }} />
              </colgroup>
              <tbody>
                {loading ?(
                  <tr>
                    <td colSpan={6} className="scb-empty">
                      Carregando base de Seção de Bloqueio...
                    </td>
                  </tr>
                ) : !rowsOrdenadas.length ?(
                  <tr>
                    <td colSpan={6} className="scb-empty">
                      Nenhuma Seção de Bloqueio encontrada.
                    </td>
                  </tr>
                ) : (
                  rowsOrdenadas.map((row) => {
                    const ativo = Number(row?.ativo) === 1;
                    return (
                      <tr key={row.id}>
                        <td>{formatSafe(row?.secao_bloqueio || row?.secaodebloqueio || row?.sb)}</td>
                        <td>{formatSafe(row?.sub)}</td>
                        <td>{formatKm(row?.km_inicial)}</td>
                        <td>{formatKm(row?.km_final)}</td>
                        <td>{formatSafe(row?.mesa_cco)}</td>
                        <td>
                          <div className="scb-actions">
                            <ToggleSwitch
                              on={ativo}
                              title={ativo ?"Inativar seção" : "Ativar seção"}
                              onToggle={() => handleToggle(row)}
                            />

                            <button
                              type="button"
                              className="scb-icon-btn"
                              title="Editar"
                              onClick={() => handleEdit(row)}
                            >
                              <IconEdit />
                            </button>

                            <button
                              type="button"
                              className="scb-icon-btn"
                              title="Excluir"
                              onClick={() => handleDelete(row)}
                            >
                              <IconTrash />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      </section>
    </div>
  );
}
